import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'fit';
  showHeader?: boolean;
  variant?: 'default' | 'dark';
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  showHeader = true,
  variant = 'default'
}) => {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [modalZIndex, setModalZIndex] = useState(60);
  // shouldRender keeps the DOM alive during the exit animation
  const [shouldRender, setShouldRender] = useState(isOpen);
  // isVisible drives the CSS transition classes
  const [isVisible, setIsVisible] = useState(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      setShouldRender(true);
      // Defer two frames: first ensures initial "closed" classes are painted,
      // second triggers the transition to the "open" state
      requestAnimationFrame(() => requestAnimationFrame(() => setIsVisible(true)));
    } else {
      setIsVisible(false);
      // Keep DOM mounted for 200 ms to let the exit animation finish
      exitTimerRef.current = setTimeout(() => setShouldRender(false), 200);
    }
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, [isOpen]);

  useEffect(() => {
    // Create or get the portal container
    let container = document.getElementById('modal-portal');
    if (!container) {
      container = document.createElement('div');
      container.id = 'modal-portal';
      container.className = 'fixed inset-0 z-[60] pointer-events-none';
      document.body.appendChild(container);
    }
    setPortalContainer(container);

    // Calculate dynamic z-index based on existing modals
    if (isOpen) {
      const existingModals = document.querySelectorAll('[data-modal-portal]');
      const newZIndex = 60 + existingModals.length;
      setModalZIndex(newZIndex);
    }

    return () => {
      setTimeout(() => {
        const existingModals = document.querySelectorAll('[data-modal-portal]');
        if (existingModals.length === 0 && container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
      }, 0);
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // iOS Safari fix: pointer-events:none on a fixed parent blocks touch events on
  // children even when they have pointer-events:auto (known WebKit bug).
  // Toggle the portal container to pointer-events:auto while any modal is rendered.
  useEffect(() => {
    const container = document.getElementById('modal-portal');
    if (!container) return;
    if (shouldRender) {
      container.style.pointerEvents = 'auto';
    } else {
      // After React unmounts this modal's DOM nodes, check if any other modal
      // is still rendered inside the container before resetting.
      const remaining = container.querySelectorAll('[data-modal-portal]');
      if (remaining.length === 0) {
        container.style.pointerEvents = '';  // revert to CSS class (pointer-events-none)
      }
    }
  }, [shouldRender]);

  if (!shouldRender || !portalContainer) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-7xl',
    full: 'max-w-[95vw]',
    fit: 'w-fit max-w-[95vw]'
  };

  const isDark = variant === 'dark';

  const modalContent = (
    <div className="fixed inset-0 flex items-center justify-center pointer-events-auto" data-modal-portal style={{ zIndex: modalZIndex }}>
      {/* Backdrop — fades in/out */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Panel — fades + scales in/out */}
      <div className={`relative ${size === 'fit' ? '' : 'w-full'} ${sizeClasses[size]} mx-4 ${size === 'full' ? 'h-[95vh] flex flex-col' : 'max-h-[95vh]'} overflow-hidden rounded-lg ${isDark ? 'bg-black/90' : 'bg-white'} shadow-xl transition-all duration-200 ease-out ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
        {/* Header */}
        {showHeader && (
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h4 className="text-gray-900">{title}</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Content */}
        <div className={`overflow-y-auto ${size === 'full' ? 'flex-1 min-h-0' : (
          showHeader
            ? (footer ? 'max-h-[calc(95vh-180px)]' : 'max-h-[calc(95vh-120px)]')
            : (footer ? 'max-h-[calc(95vh-120px)]' : 'max-h-[calc(95vh-60px)]')
        )}`}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, portalContainer);
};
